/**
 * `TRACE_materials_occlusion` — glTF loader extension for depth-only
 * (occluder) materials.
 *
 * Design: `docs/GLTF_TRACE_MATERIALS_OCCLUSION.md`. The extension is
 * presence-as-flag: a material carrying it writes depth and no colour, so
 * virtual content behind the mesh is hidden and the AR camera feed (or the
 * editor background) shows through where the mesh itself would be. The
 * material's PBR block is a deliberate fallback — clients that do not know
 * the extension render matte black — and it is also what the editor shows.
 *
 * This module does two things:
 *
 * 1. Registers the loader extension (side effect on import, the same way
 *    Babylon's own `KHR_materials_unlit` registers). It lets the default PBR
 *    properties load — that is the edit-mode look and the thumbnail — then
 *    pins the depth/culling flags and marks the material. It does NOT set
 *    `disableColorWrite`: depth-only is a play-mode / XR behaviour and the
 *    switch is owned by {@link OccluderMode}, so an occluder is visible and
 *    selectable in the editor.
 *
 * 2. Exposes the marker predicate the runtime (play-mode gate, opaque sort,
 *    exporter) uses to recognise an occluder. The marker lives in
 *    `material.metadata`, which `Material.clone` carries over, so
 *    `Renderer`'s per-object clones stay occluders.
 *
 * The material stays a `PBRMaterial` rather than Babylon's `OcclusionMaterial`
 * (a `ShaderMaterial`): Renderer tint/factor code and MaterialPreviews narrow
 * on PBR, and an occluder that is still PBR falls through them harmlessly
 * because its colour is never written in play mode.
 */
import { Material } from "@babylonjs/core/Materials/material";
import { Observable } from "@babylonjs/core/Misc/observable";
import type { Nullable } from "@babylonjs/core/types";
import { GLTFLoader } from "@babylonjs/loaders/glTF/2.0/glTFLoader";
import { registerGLTFExtension, unregisterGLTFExtension } from "@babylonjs/loaders/glTF/2.0/glTFLoaderExtensionRegistry";
import type { IGLTFLoaderExtension } from "@babylonjs/loaders/glTF/2.0/glTFLoaderExtension";
import type { IMaterial } from "@babylonjs/loaders/glTF/2.0/glTFLoaderInterfaces";

/** The glTF extension name, as it appears in `extensionsUsed` and `material.extensions`. */
export const TRACE_MATERIALS_OCCLUSION = "TRACE_materials_occlusion";

/** Shape of `material.metadata` once an occluder is marked. */
interface OccluderMetadata {
    gltf?: {
        extensions?: Record<string, unknown>;
    };
}

/**
 * Fires after a material is marked. The loader marks asynchronously — after
 * the base PBR properties have loaded — so for a direct-to-scene load
 * (`ImportMeshAsync`) the material can join the scene before the marker
 * lands; the play-mode gate listens here so the order does not matter.
 */
export const onOccluderMarkedObservable = new Observable<Material>();

/**
 * Mark a Babylon material as an occluder. Idempotent. `payload` is the
 * extension object from the glTF (`{}` today; kept so fields added later
 * survive a round trip through the exporter).
 */
export function markOccluderMaterial(material: Material, payload: object = {}): void {
    const metadata = (material.metadata ??= {}) as OccluderMetadata;
    const gltf = (metadata.gltf ??= {});
    const extensions = (gltf.extensions ??= {});
    extensions[TRACE_MATERIALS_OCCLUSION] = { ...payload };
    onOccluderMarkedObservable.notifyObservers(material);
}

/**
 * Remove the occluder marker from a material WITHOUT mutating metadata it may
 * share with another material.
 *
 * `Material.clone` copies `metadata` by REFERENCE, so a clone of a container's
 * source material points at the very same metadata object. Deleting the marker
 * in place would strip it from the source too, and every later clone of that
 * cached container would silently stop being an occluder — occlusion would
 * break scene-wide, long after the call that caused it. So rebuild the chain
 * instead of editing it.
 *
 * Used by the material-asset preview path: a preview deliberately shows the
 * PBR fallback (this module's "edit-mode look"), so the throwaway clone on the
 * preview sphere must be invisible to {@link OccluderMode} — otherwise a
 * preview taken during play mode renders depth-only, i.e. nothing at all.
 */
export function unmarkOccluderMaterial(material: Material): void {
    const metadata = material.metadata as OccluderMetadata | null | undefined;
    const gltf = metadata?.gltf;
    if (!gltf?.extensions || gltf.extensions[TRACE_MATERIALS_OCCLUSION] === undefined) return;
    const extensions = { ...gltf.extensions };
    delete extensions[TRACE_MATERIALS_OCCLUSION];
    material.metadata = { ...metadata, gltf: { ...gltf, extensions } };
}

/** True when the material carries the occluder marker (source or clone). */
export function isOccluderMaterial(material: Nullable<Material> | undefined): boolean {
    const metadata = material?.metadata as OccluderMetadata | null | undefined;
    return metadata?.gltf?.extensions?.[TRACE_MATERIALS_OCCLUSION] !== undefined;
}

/**
 * Pin the properties an occluder needs regardless of what else the glTF
 * material says. Colour write is deliberately left alone (see module doc).
 */
export function applyOccluderMaterialProperties(material: Material, doubleSided: boolean): void {
    // Depth must land even if something upstream marked the material
    // transparent; an occluder that skips depth occludes nothing.
    material.forceDepthWrite = true;
    // Culling is the one glTF property honoured beyond the fallback look: a
    // library occluder is double-sided (creator planes face either way), a
    // closed scanned shell can be single-sided.
    material.backFaceCulling = !doubleSided;
    // An occluder in the transparent queue would draw after opaque geometry —
    // depth written, colour already there, nothing hidden — and be sorted
    // back-to-front instead of occluder-first. Depth-only has no alpha to
    // honour, so force the opaque queue.
    material.transparencyMode = Material.MATERIAL_OPAQUE;
    material.alpha = 1;
}

/**
 * [Specification](../../../docs/GLTF_TRACE_MATERIALS_OCCLUSION.md)
 */
// eslint-disable-next-line @typescript-eslint/naming-convention
export class TRACE_materials_occlusion implements IGLTFLoaderExtension {
    /** The name of this extension. */
    public readonly name = TRACE_MATERIALS_OCCLUSION;

    /** Defines whether this extension is enabled. */
    public enabled: boolean;

    /**
     * Order among loader extensions. Babylon sorts ascending and the FIRST
     * extension whose `loadMaterialPropertiesAsync` returns a promise wins —
     * later ones never run. So this must sort BEFORE `KHR_materials_unlit`
     * (210), not after: from here the inner `loadMaterialPropertiesAsync`
     * re-enters the chain (the loader's re-entry guard skips only this
     * extension), unlit applies, and the occluder flags are pinned on top.
     * At a higher order an unlit+occluder material was silently never marked.
     */
    public order = 200;

    private _loader: GLTFLoader;

    /** @internal */
    constructor(loader: GLTFLoader) {
        this._loader = loader;
        this.enabled = this._loader.isExtensionUsed(TRACE_MATERIALS_OCCLUSION);
    }

    /** @internal */
    public dispose(): void {
        this._loader = null!;
    }

    /** @internal */
    public loadMaterialPropertiesAsync(context: string, material: IMaterial, babylonMaterial: Material): Nullable<Promise<void>> {
        return GLTFLoader.LoadExtensionAsync<object, void>(context, material, this.name, async (_extensionContext, extension) => {
            // Default PBR properties first: the black/rough block is the
            // fallback look, the edit-mode look, and the thumbnail. The loader
            // guards against re-entering this extension for the same call.
            await this._loader.loadMaterialPropertiesAsync(context, material, babylonMaterial);
            applyOccluderMaterialProperties(babylonMaterial, material.doubleSided === true);
            markOccluderMaterial(babylonMaterial, extension);
        });
    }
}

unregisterGLTFExtension(TRACE_MATERIALS_OCCLUSION);
registerGLTFExtension(TRACE_MATERIALS_OCCLUSION, true, (loader) => new TRACE_materials_occlusion(loader));
